import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/App";
import { Car, CheckCircle, Columns, Package, PackageCheck, AlertTriangle, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format, differenceInDays, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

// Utility function for urgency
const getUrgency = (vehicle) => {
  if (!vehicle.date_rdv) return "normal";
  const days = differenceInDays(new Date(), parseISO(vehicle.date_rdv));
  if (days > 3) return "urgent";
  if (days > 0) return "soon";
  return "normal";
};

// Vehicle card component - same style as Planning
function KanbanVehicleCard({ vehicle, stepName, mobile, compact, navigate }) {
  const urgency = getUrgency(vehicle);
  const isPriority = vehicle.priorite && vehicle.type_intervention !== "mecanique" && (
    !vehicle.priorite_semaine || new Date(vehicle.priorite_semaine) <= new Date()
  );
  
  // Mode compact pour mobile
  if (compact) {
    return (
      <div
        onClick={() => navigate(`/vehicles/${vehicle.id}`)}
        className={`rounded-lg border p-1.5 cursor-pointer transition-all hover:shadow-md relative ${
          isPriority ? "ring-2 ring-red-500 ring-offset-1" :
          urgency === "urgent" ? "bg-red-50 border-red-300" :
          urgency === "soon" ? "bg-amber-50 border-amber-300" :
          "bg-white border-slate-200"
        }`}
      >
        {/* Gommette priorité */}
        {isPriority && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" title="Prioritaire" />
        )}
        
        {/* Client + Urgence */}
        <div className="flex items-center justify-between gap-1">
          <span className="text-[10px] font-semibold text-slate-800 truncate">
            {vehicle.client?.nom || "?"}
          </span>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {urgency === "urgent" && <AlertTriangle className="h-3 w-3 text-red-500" />}
            {vehicle.pieces_commandees ? (
              <PackageCheck className="h-3 w-3 text-emerald-500" />
            ) : (
              <Package className="h-3 w-3 text-red-500" />
            )}
          </div>
        </div>
        
        {/* Marque + Modèle */}
        {(vehicle.marque || vehicle.modele) && (
          <p className="text-[9px] text-slate-600 truncate">
            {vehicle.marque} {vehicle.modele}
          </p>
        )}
        
        {/* Description/Notes */}
        {vehicle.description && (
          <p className="text-[8px] text-slate-500 truncate italic">
            {vehicle.description}
          </p>
        )}
        
        {/* Immat */}
        <p className="font-mono text-[9px] font-bold text-slate-700 truncate mt-0.5">
          {vehicle.immatriculation}
        </p>
      </div>
    );
  }
  
  // Mode normal (desktop et mobile large)
  return (
    <div
      onClick={() => navigate(`/vehicles/${vehicle.id}`)}
      className={`rounded-lg border p-2.5 cursor-pointer transition-all hover:shadow-md relative ${
        isPriority ? "ring-2 ring-red-500 ring-offset-1" :
        urgency === "urgent" ? "bg-red-50 border-red-300" :
        urgency === "soon" ? "bg-amber-50 border-amber-300" :
        "bg-white border-slate-200"
      }`}
    >
      {/* Gommette priorité */}
      {isPriority && (
        <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full border-2 border-white" title="Prioritaire" />
      )}
      
      {/* Ligne 1: Client + Urgence + Pièces */}
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-slate-800 truncate text-sm">
          {vehicle.client?.nom || "?"} {vehicle.client?.prenom || ""}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {urgency === "urgent" && <AlertTriangle className="h-4 w-4 text-red-500" />}
          {urgency === "soon" && <Clock className="h-4 w-4 text-amber-500" />}
          {vehicle.pieces_commandees ? (
            <PackageCheck className="h-4 w-4 text-emerald-500" title="Pièces commandées" />
          ) : (
            <Package className="h-4 w-4 text-red-500" title="Pièces à commander" />
          )}
        </div>
      </div>
      
      {/* Ligne 2: Marque + Modèle */}
      {(vehicle.marque || vehicle.modele) && (
        <p className="text-xs text-slate-600 truncate mt-1">
          {vehicle.marque} {vehicle.modele}
        </p>
      )}
      
      {/* Ligne 3: Description/Notes */}
      {vehicle.description && (
        <p className="text-xs text-slate-500 truncate italic mt-0.5">
          {vehicle.description}
        </p>
      )}
      
      {/* Ligne 4: Immat + Date */}
      <div className="flex items-center justify-between gap-2 mt-1.5">
        <span className="font-mono font-bold text-slate-700 text-xs">
          {vehicle.immatriculation}
        </span>
        {vehicle.date_rdv && (
          <span className="text-xs text-slate-400">
            {format(parseISO(vehicle.date_rdv), "d MMM", { locale: fr })}
          </span>
        )}
      </div>
    </div>
  );
}

export default function KanbanView() {
  const [kanbanData, setKanbanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchKanban = async () => {
      setLoading(true);
      try {
        const response = await api.get("/kanban");
        setKanbanData(response.data);
        if (response.data?.columns) {
          const firstWithVehicles = Object.entries(response.data.columns).find(([_, col]) => col.vehicles?.length > 0);
          if (firstWithVehicles) {
            setActiveStep(firstWithVehicles[0]);
          } else if (response.data.steps?.[0]) {
            setActiveStep(response.data.steps[0].name);
          }
        }
      } catch (error) {
        toast.error("Erreur lors du chargement");
      }
      setLoading(false);
    };
    fetchKanban();
  }, []);

  const getStepColor = (color) => {
    const colors = {
      amber: "bg-amber-500",
      orange: "bg-orange-500",
      blue: "bg-blue-500",
      purple: "bg-purple-500",
      emerald: "bg-emerald-500",
      red: "bg-red-500",
      pink: "bg-pink-500",
      cyan: "bg-cyan-500",
    };
    return colors[color] || "bg-slate-500";
  };

  const getStepBgColor = (color) => {
    const colors = {
      amber: "bg-amber-50/50",
      orange: "bg-orange-50/50",
      blue: "bg-blue-50/50",
      purple: "bg-purple-50/50",
      emerald: "bg-emerald-50/50",
      red: "bg-red-50/50",
      pink: "bg-pink-50/50",
      cyan: "bg-cyan-50/50",
    };
    return colors[color] || "bg-slate-50/50";
  };

  if (loading) {
    return (
      <div className="p-4 flex justify-center items-center h-[calc(100vh-80px)]">
        <div className="spinner" />
      </div>
    );
  }

  if (!kanbanData) return null;

  const columns = kanbanData.columns;
  
  // Pour les collaborateurs (non admin), exclure "terminé"
  const isAdmin = user?.role === "admin" || user?.specialite === "secretaire";
  const stepOrder = isAdmin 
    ? [...kanbanData.steps.map(s => s.name), "termine"]
    : kanbanData.steps.map(s => s.name);
  
  const totalVehicles = stepOrder.reduce((acc, step) => acc + (columns[step]?.vehicles?.length || 0), 0);

  // Mobile navigation
  const currentStepIndex = stepOrder.indexOf(activeStep);
  const goToPrevStep = () => {
    if (currentStepIndex > 0) setActiveStep(stepOrder[currentStepIndex - 1]);
  };
  const goToNextStep = () => {
    if (currentStepIndex < stepOrder.length - 1) setActiveStep(stepOrder[currentStepIndex + 1]);
  };

  // Desktop column renderer
  const renderDesktopColumn = (stepName, isMobileView = false) => {
    const column = columns[stepName];
    if (!column) return null;
    
    const step = column.step;
    const vehicles = column.vehicles || [];
    
    const sortedVehicles = [...vehicles].sort((a, b) => {
      const urgencyOrder = { urgent: 0, soon: 1, normal: 2 };
      return (urgencyOrder[getUrgency(a)] ?? 2) - (urgencyOrder[getUrgency(b)] ?? 2);
    });
    
    return (
      <div key={stepName} className={`flex flex-col overflow-hidden ${isMobileView ? 'min-w-[140px] w-[140px]' : 'min-w-0 flex-1'}`}>
        <div className={`rounded-t-lg px-2 md:px-3 py-1.5 md:py-2 ${getStepColor(step.color)} text-white flex-shrink-0`}>
          <div className="flex items-center justify-between gap-1">
            <h3 className="font-bold text-[10px] md:text-sm truncate">{step.label}</h3>
            <Badge className="bg-white/20 text-white text-[10px] md:text-xs px-1.5 md:px-2">{vehicles.length}</Badge>
          </div>
        </div>
        
        <div className={`flex-1 rounded-b-lg border border-t-0 border-slate-200 overflow-y-auto p-1.5 md:p-2 space-y-1.5 md:space-y-2 ${isMobileView ? 'min-h-[120px] max-h-[150px]' : 'min-h-[150px] max-h-[280px]'} ${getStepBgColor(step.color)}`}>
          {vehicles.length === 0 ? (
            <div className="flex items-center justify-center h-full text-slate-300">
              <Car className="h-6 w-6 md:h-8 md:w-8 opacity-30" />
            </div>
          ) : (
            sortedVehicles.map((vehicle) => (
              <KanbanVehicleCard key={vehicle.id} vehicle={vehicle} stepName={stepName} navigate={navigate} compact={isMobileView} />
            ))
          )}
        </div>
      </div>
    );
  };

  // Split for 2 rows
  const columnsPerRow = Math.ceil(stepOrder.length / 2);
  const row1Steps = stepOrder.slice(0, columnsPerRow);
  const row2Steps = stepOrder.slice(columnsPerRow);

  // Active column for mobile swipe
  const activeColumn = columns[activeStep];
  const activeStepInfo = activeColumn?.step;
  const activeVehicles = activeColumn?.vehicles || [];

  return (
    <div className="p-2 md:p-4 h-[calc(100vh-64px)] md:h-[calc(100vh-64px)] flex flex-col animate-fade-in" data-testid="kanban-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 md:mb-3 flex-shrink-0">
        <div className="flex items-center gap-2 md:gap-3">
          <Columns className="h-5 w-5 md:h-7 md:w-7 text-slate-700" />
          <h1 className="text-base md:text-2xl font-black text-slate-900">Kanban</h1>
          <Badge variant="outline" className="text-xs md:text-sm">
            {totalVehicles} en cours
          </Badge>
        </div>
      </div>

      {/* MOBILE VIEW - 2 rows with horizontal scroll */}
      <div className="md:hidden flex-1 flex flex-col min-h-0 gap-2 overflow-hidden">
        {/* Row 1 */}
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div className="flex gap-2 h-full pb-2" style={{ minWidth: 'max-content' }}>
            {row1Steps.map(stepName => renderDesktopColumn(stepName, true))}
          </div>
        </div>
        {/* Row 2 */}
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div className="flex gap-2 h-full pb-2" style={{ minWidth: 'max-content' }}>
            {row2Steps.map(stepName => renderDesktopColumn(stepName, true))}
          </div>
        </div>
      </div>

      {/* DESKTOP VIEW - 2 rows grid */}
      <div className="hidden md:flex flex-1 flex-col gap-4 min-h-0 overflow-auto">
        <div className="flex-1 min-h-0">
          <div className="grid gap-3 h-full" style={{ gridTemplateColumns: `repeat(${row1Steps.length}, 1fr)` }}>
            {row1Steps.map(stepName => renderDesktopColumn(stepName, false))}
          </div>
        </div>
        
        <div className="flex-1 min-h-0">
          <div className="grid gap-3 h-full" style={{ gridTemplateColumns: `repeat(${row2Steps.length}, 1fr)` }}>
            {row2Steps.map(stepName => renderDesktopColumn(stepName, false))}
          </div>
        </div>
      </div>
    </div>
  );
}
